import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';

const Services = () => {
  const services = [
    {
      title: 'Psychiatric Evaluation',
      description: 'Comprehensive initial assessment to understand your mental health needs, history, and develop an individualized treatment approach.'
    },
    {
      title: 'Psychiatric Medication Management',
      description: 'Expert oversight of psychiatric medications, monitoring effectiveness, adjusting dosages, and managing side effects for optimal outcomes.'
    },
    {
      title: 'Mood Disorder Treatment',
      description: 'Specialized care for depression, bipolar disorders, irritability and other mood conditions like PMDD and postpartum depression using evidence-based medication and therapeutic strategies.'
    },
    {
      title: 'Anxiety & Trauma Care',
      description: 'Integrated treatment for anxiety disorders, PTSD, panic disorder, and trauma-related conditions with compassionate, whole-person focus.'
    },
    {
      title: 'Binge Eating & Impulse Regulation',
      description: 'Targeted psychiatric support for binge eating disorder and impulse control challenges, addressing both neurological and behavioral factors.'
    },
    {
      title: 'Integrated Medication Support',
      description: 'Holistic medication management considering metabolic health, side effect management such as medication-induced weight gain and sexual dysfunction, hormone support for men and women, and overall wellbeing for sustainable treatment outcomes.'
    },
    {
      title: 'ADHD and Poor Executive Functioning',
      description: 'Comprehensive assessment and treatment for attention deficit hyperactivity disorder and executive function challenges affecting daily life and productivity.'
    },
    {
      title: 'Caregiver Stress and Burnout',
      description: 'Specialized support for caregivers experiencing emotional exhaustion, compassion fatigue, and burnout from caring for loved ones.'
    },
    {
      title: 'Workplace Stress',
      description: 'Treatment for work-related stress, burnout, and occupational mental health challenges affecting your professional and personal wellbeing.'
    }
  ];

  return (
    <main className="pt-24 bg-[#FDFDFB]">
      <SEO 
        title="Services - Comprehensive Psychiatric Care"
        description="Expert psychiatric evaluation, medication management, mood disorder treatment, anxiety care, and integrated support services."
        keywords="psychiatric evaluation, medication management, mood disorders, anxiety treatment, trauma care, binge eating treatment"
      />

      <section className="py-16 bg-gradient-to-b from-[#f7f4f0] to-[#FDFDFB]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-5xl font-bold text-[#7a8a72] mb-6">Our Services</h1>
            <p className="text-xl text-[#3a3a3a]">
              Comprehensive psychiatric care tailored to your unique needs
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="p-8 bg-white border-[#d4cec5] hover:shadow-lg transition-all">
                <CheckCircle2 className="text-[#7a8a72] mb-4" size={32} />
                <h3 className="text-2xl font-bold text-[#7a8a72] mb-3">{service.title}</h3>
                <p className="text-[#3a3a3a] leading-relaxed">{service.description}</p>
              </Card>
            ))}
          </div>

          {/* Ketamine-Assisted Psychiatric Treatment Section */}
          <Card className="mt-12 max-w-5xl mx-auto p-8 bg-[#f7f4f0] border-[#7a8a72] border-2">
            <h2 className="text-3xl font-bold text-[#7a8a72] mb-4">Ketamine-Assisted Psychiatric Treatment</h2>
            <p className="text-[#3a3a3a] leading-relaxed mb-6">
              Ketamine-based treatment is offered as part of integrated psychiatric care for patients with documented treatment-resistant mood disorders or established longitudinal care within the practice. This service is not offered as stand-alone or drop-in treatment and requires a comprehensive psychiatric evaluation, medical clearance, and individualized treatment planning.
            </p>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-[#7a8a72] mb-3">Eligibility Criteria:</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-[#3a3a3a]">
                  <CheckCircle2 className="text-[#7a8a72] flex-shrink-0 mt-0.5" size={20} />
                  <span>Documented history of treatment failure</span>
                </li>
                <li className="flex items-start gap-2 text-[#3a3a3a]">
                  <CheckCircle2 className="text-[#7a8a72] flex-shrink-0 mt-0.5" size={20} />
                  <span>Established patients in care</span>
                </li>
                <li className="flex items-start gap-2 text-[#3a3a3a]">
                  <CheckCircle2 className="text-[#7a8a72] flex-shrink-0 mt-0.5" size={20} />
                  <span>Complex or treatment-resistant psychiatric conditions</span>
                </li>
                <li className="flex items-start gap-2 text-[#3a3a3a]">
                  <CheckCircle2 className="text-[#7a8a72] flex-shrink-0 mt-0.5" size={20} />
                  <span>Longitudinal psychiatric management cases</span>
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-[#7a8a72] mb-3">Clinical Integration:</h3>
              <p className="text-[#3a3a3a]">
                When clinically appropriate, care may be coordinated with the patient's existing therapist to support psychotherapy integration and continuity of treatment.
              </p>
            </div>

            <div className="bg-[#fff9e6] p-4 rounded-lg mb-6">
              <p className="text-[#3a3a3a] font-semibold">
                Ketamine assisted therapy is offered on a self-pay basis only and is not typically covered by insurance.
              </p>
            </div>

            <div className="text-center">
              <Link to="/book-appointment">
                <Button size="lg" className="bg-[#7a8a72] hover:bg-[#6a7a62] text-white rounded-lg">
                  Request Ketamine Initial Consultation
                </Button>
              </Link>
            </div>
          </Card>

          <Card className="mt-12 max-w-5xl mx-auto p-6 bg-[#fff9e6] border-[#c9b17a]">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-[#c9b17a] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-[#3a3a3a] mb-2">Billing Clarity</h3>
                <p className="text-[#3a3a3a]">
                  Some integrated services may be offered on a self-pay basis when clinically indicated and not covered by insurance. All care options are discussed transparently prior to initiation.
                </p>
              </div>
            </div>
          </Card>

          <div className="text-center mt-12">
            <Link to="/book-appointment">
              <Button size="lg" className="bg-[#c9b17a] hover:bg-[#b89d66] text-white text-xl px-12 py-6 rounded-lg shadow-lg">
                Now Accepting New Clients — Book Today
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;